import { useState, useRef, useEffect } from 'react';

interface User {
  id: string;
  name: string;
  username: string;
  avatar: string;
  level: number;
}

interface MentionsAndTagsProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export default function MentionsAndTags({
  value,
  onChange,
  placeholder = 'Escreva um comentário...',
  className = ''
}: MentionsAndTagsProps) {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const users: User[] = [
    { id: '1', name: 'Maria Silva', username: 'maria.silva', avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20smiling%20portrait%20business%20casual%20friendly%20warm%20lighting%20studio%20background&width=100&height=100&seq=mention1&orientation=squarish', level: 12 },
    { id: '2', name: 'João Santos', username: 'joao.santos', avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20smiling%20portrait%20business%20casual%20confident%20natural%20lighting%20studio%20background&width=100&height=100&seq=mention2&orientation=squarish', level: 15 },
    { id: '3', name: 'Ana Costa', username: 'ana.costa', avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20smiling%20portrait%20business%20attire%20elegant%20bright%20lighting%20studio%20background&width=100&height=100&seq=mention3&orientation=squarish', level: 10 },
    { id: '4', name: 'Carlos Oliveira', username: 'carlos.oliveira', avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20smiling%20portrait%20business%20suit%20confident%20professional%20lighting%20studio%20background&width=100&height=100&seq=mention4&orientation=squarish', level: 18 }
  ];

  const filteredUsers = users.filter(
    user =>
      user.name.toLowerCase().includes(mentionSearch.toLowerCase()) ||
      user.username.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    
    onChange(newValue);
    setCursorPosition(cursorPos);

    // Check for @ mention
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      if (!textAfterAt.includes(' ')) {
        setMentionSearch(textAfterAt);
        setShowMentions(true);
        return;
      }
    }
    
    setShowMentions(false);
  };

  const insertMention = (user: User) => {
    const textBeforeCursor = value.substring(0, cursorPosition);
    const textAfterCursor = value.substring(cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    const newValue =
      textBeforeCursor.substring(0, lastAtIndex) +
      `@${user.username} ` +
      textAfterCursor;
    
    onChange(newValue);
    setShowMentions(false);
    
    // Focus back on textarea
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const insertHashtag = () => {
    const newValue = value + '#';
    onChange(newValue);
    textareaRef.current?.focus();
  };

  // Parse text to highlight mentions and hashtags
  const renderFormattedText = () => {
    const parts = value.split(/(@\w+|#\w+)/g);
    return parts.map((part, index) => {
      if (part.startsWith('@')) {
        return (
          <span key={index} className="text-teal-600 font-semibold">
            {part}
          </span>
        );
      }
      if (part.startsWith('#')) {
        return (
          <span key={index} className="text-cyan-600 font-semibold">
            {part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <div className={`relative ${className}`}>
      {/* Textarea */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
          rows={3}
        />
        
        {/* Toolbar */}
        <div className="absolute bottom-2 right-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              onChange(value + '@');
              textareaRef.current?.focus();
            }}
            className="p-2 text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
            title="Mencionar usuário"
          >
            <i className="ri-at-line text-xl"></i>
          </button>
          <button
            type="button"
            onClick={insertHashtag}
            className="p-2 text-gray-600 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
            title="Adicionar hashtag"
          >
            <i className="ri-hashtag text-xl"></i>
          </button>
        </div>
      </div>

      {/* Mentions Dropdown */}
      {showMentions && filteredUsers.length > 0 && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowMentions(false)}
          ></div>
          <div className="absolute bottom-full left-0 mb-2 w-full bg-white rounded-xl shadow-2xl z-50 max-h-64 overflow-y-auto">
            <div className="p-2">
              <p className="text-xs text-gray-500 px-3 py-2 font-medium">
                Mencionar usuário
              </p>
              {filteredUsers.map(user => (
                <button
                  key={user.id}
                  onClick={() => insertMention(user)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="relative flex-shrink-0">
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-white">
                      {user.level}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">
                      {user.name}
                    </p>
                    <p className="text-sm text-gray-600 truncate">
                      @{user.username}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Preview of formatted text */}
      {value && (
        <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
          <p className="text-xs text-gray-500 mb-1 font-medium">Preview:</p>
          <div className="whitespace-pre-wrap break-words">
            {renderFormattedText()}
          </div>
        </div>
      )}
    </div>
  );
}
